import { reactBridge } from "@medflyt/webapp-react/src/core";
import {
  AngularJSDispatch,
  AngularJSPayload,
} from "@medflyt/webapp-react/src/core/react-bridge";
import { TicketPopupOptions } from "@medflyt/webapp-react/src/modules/communication/components/CommCenterChatWrapper";
import { EntityCommunicationsProps } from "@medflyt/webapp-react/src/modules/communication/components/entity-communications/EntityCommunications";
import { GlobalWorkflowHint } from "@medflyt/webapp-react/src/shared/hooks/useGlobalWorkflowRunner";
import { NoteId, PatientId } from "@medflyt/webapp-react/src/shared/schema/schema";
import { NewTicketPopupProps } from "webapp-react/src/modules/communication/components/NewTicketPopup";
import { OpenSmsSenderData } from "webapp-react/src/shared/components/ReactHost/SmsSenderListener";
import { NewRnPlatformEmailPopupProps } from "webapp-react/src/shared/hooks/useNewRnPlatformEmail";
import type { DatabaseApiService } from "./services/db";

const createAngularToReactEvents = () => {
  return {
    workflows: {
      openRunner: () => {
        return reactBridge.dispatch("GLOBAL_WORKFLOW_RUNNER:OPEN", undefined);
      },
      setHints: (hints: GlobalWorkflowHint[]) => {
        return reactBridge.dispatch("GLOBAL_WORKFLOW_RUNNER:SET_HINTS", { hints });
      },
    },
    sms: {
      openSender: (data: OpenSmsSenderData) => {
        return reactBridge.dispatch("OPEN_SMS_SENDER", data);
      },
    },
    note: {
      openEditNoteModal: (noteId: NoteId) => {
        return reactBridge.dispatch("EDIT_NOTE_MODAL:OPEN", { id: noteId });
      },
    },
    commCenter: {
      openTicketPopup: (params: TicketPopupOptions) => {
        return reactBridge.dispatch("COMM_CENTER:OPEN_TICKET_POPUP", params);
      },
      openNewTicketPopup: (params: NewTicketPopupProps) => {
        return reactBridge.dispatch("COMM_CENTER:OPEN_NEW_TICKET_POPUP", params);
      },
      openEntityCommunications: (params: EntityCommunicationsProps) => {
        return reactBridge.dispatch("COMM_CENTER:OPEN_ENTITY_COMMUNICATIONS", params);
      },
      openNewRnPlatformEmailPopup: (params: NewRnPlatformEmailPopupProps) => {
        return reactBridge.dispatch("COMM_CENTER:OPEN_NEW_RN_PLATFORM_EMAIL_POPUP", params);
      },
    },
    callCenter: {
      openDialer: (params: { predefinedPhoneNumber?: string } = {}) => {
        return reactBridge.dispatch("CALL_CENTER:OPEN_DIALER_POPUP", params);
      },
      closeDialer: () => {
        return reactBridge.dispatch("CALL_CENTER:CLOSE_DIALER_POPUP", undefined);
      },
      openRingingCalls: () => {
        return reactBridge.dispatch("CALL_CENTER:OPEN_RINGING_CALLS", undefined);
      },
    },
    customAddress: {
      openModal: (props: AngularJSPayload["OPEN_CUSTOMIZE_ADDRESS_MODAL"]) => {
        return reactBridge.dispatch("CUSTOM_ADDRESS:OPEN_MODAL", props);
      },
      onChange: (props: AngularJSPayload["CUSTOM_ADDRESS_CHANGE"]) => {
        return reactBridge.dispatch("CUSTOM_ADDRESS:ON_CHANGE", props);
      },
      openVisitInstanceModal: (
        props: AngularJSDispatch["CUSTOM_ADDRESS:OPEN_VISIT_INSTANCE_MODAL"]
      ) => {
        return reactBridge.dispatch("CUSTOM_ADDRESS:OPEN_VISIT_INSTANCE_MODAL", props);
      },
    },
    patient: {
      openTaskActivity: (patientId: PatientId) => {
        return reactBridge.dispatch("PATIENT:OPEN_TASK_ACTIVITY", { patientId });
      },
      openSyncVisitsAddress: (patientId: PatientId) => {
        return reactBridge.dispatch("PATIENT:OPEN_SYNC_VISITS_ADDRESS", { patientId });
      },
    },
  };
};

declare module "angular" {
  interface IRootScopeService {
    react: ReturnType<typeof createAngularToReactEvents>;
  }
}

//! @ngInject
export function angularToReact(
  $rootScope: angular.IRootScopeService,
  $uibModal: angular.ui.bootstrap.IModalService,
  DatabaseApi: DatabaseApiService,
  toaster: any,
  $filter: angular.IFilterService
) {
  $rootScope.react = createAngularToReactEvents();

  async function prepareDocumentReplaceConstants(
    patientName: string,
    caregiverName: string | null
  ) {
    return {
      agencyName: ($rootScope as any).user?.agency?.name,
      caregiverName: caregiverName,
      patientName: patientName,
      agencyAddress: ($rootScope as any).user?.agency?.address,
      agencyPhone: ($filter("americanphone") as (v: unknown) => string)(
        ($rootScope as any).user?.agency?.officePhone
      ),
    };
  }

  reactBridge.onDispatch("OPEN_PATIENT_DOCUMENT_REVIEW_MODAL", (payload) => {
    const { taskInstanceId, documentTypeId, patientId, patientName, caregiverName } = payload;
    const agencyId = ($rootScope as any).agencyId;
    const agencyMemberId = ($rootScope as any).agencyMemberId;
    const url = `agencies/${agencyId}/agency_members/${agencyMemberId}/patient_task_instances/${taskInstanceId}/patient_documents/${documentTypeId}`;

    (DatabaseApi as any).get(url).then(
      function (res: any) {
        $uibModal.open({
          templateUrl: "admin/views/edit-patient-doc-modal.html",
          size: "lg",
          controller: "editPatientDocModalCtrl",
          resolve: {
            patientId: () => patientId as PatientId,
            document: () => res.data.document,
            documentReplaceConstants: () =>
              prepareDocumentReplaceConstants.bind(null, patientName, caregiverName),
          },
        });
      },
      function () {
        toaster.pop("error", "Something went wrong");
      }
    );
  });
}
